'use client'

import { GridSection } from '@/components/marketing/fragments/grid-section'
import { SiteHeading } from '@/components/marketing/fragments/site-heading'
import { StarBorder } from '@/components/ui/star-border'
import { Routes } from '@/constants/routes'
import { msg } from '@lingui/core/macro'
import { useLingui } from '@lingui/react'
import Image from 'next/image'
import Link from 'next/link'
import * as React from 'react'

type SolutionItem = {
  title: ReturnType<typeof msg>
  description: ReturnType<typeof msg>
  features: Array<ReturnType<typeof msg>>
  icon: string
  href: string
  comingSoon?: boolean
}

const solutionsList: SolutionItem[] = [
  {
    title: msg`Pione Trace`,
    description: msg`Digital infrastructure for identification, authentication, traceability, and tokenization of Real-World Assets (RWA) in agriculture, commodities, and real estate. Built to help enterprises verify provenance, reduce fraud risk, and accelerate compliant digitalization at scale.`,
    features: [
      msg`Enterprise Asset Tokenization`,
      msg`Multi-layer Authentication`,
      msg`Supply Chain Tracking`,
      msg`Advanced Anti-counterfeiting`,
      msg`Interoperable API Gateway`
    ],
    icon: '/landing/solutions/pione-trace.svg',
    href: 'https://pionetrace.com'
  },
  {
    title: msg`Pione Farm`,
    description: msg`Agricultural product traceability platform from production to consumption, helping businesses and cooperatives transparentize the supply chain, enhance brand value, and strengthen market trust in the agricultural sector.`,
    features: [
      msg`Seed-to-Shelf Monitoring`,
      msg`Quality & Compliance Tracking`,
      msg`Brand Value Enhancement`,
      msg`IoT Farm Integration`,
      msg`Export Quality Assurance`
    ],
    icon: '/landing/solutions/pione-farm.svg',
    href: 'https://pionefarm.com'
  },
  {
    title: msg`Pione Wallet`,
    description: msg`Digital asset wallet helping businesses and users manage, store, and transact digital assets securely, conveniently, and safely – it's a gateway to the global decentralized financial ecosystem. Designed for everyday usability while maintaining strong security for both individuals and organizations.`,
    features: [
      msg`Advanced Non-custodial Security`,
      msg`Cross-chain Asset Swaps`,
      msg`Staking & Governance Portal`,
      msg`Unified Identity Sync`,
      msg`Institutional Asset Management`
    ],
    icon: '/landing/solutions/pione-wallet.svg',
    href: 'https://pionewallet.com'
  },
  {
    title: msg`Pione AI`,
    description: msg`AI tool providing personalized support for small and medium-sized enterprises in customer care, management and business operations. Helps teams automate repetitive tasks, surface actionable insights, and scale service quality with consistent responses.`,
    features: [
      msg`Predictive Data Analytics`,
      msg`Custom AI Agents (RAG)`,
      msg`Natural Language Processing`,
      msg`Workflow Optimization`,
      msg`Scalable Cognitive Processing`
    ],
    icon: '/landing/solutions/pione-ai.svg',
    href: 'https://pioneai.com'
  },
  {
    title: msg`Pione House`,
    description: msg`Blockchain application solution for real estate identification and authentication, aiming to enhance transparency, reduce risks, and optimize transaction processes in the real estate market. Enables verifiable property records and more efficient due diligence for stakeholders across the ecosystem.`,
    features: [
      msg`Fractional Property Trading`,
      msg`Digital Title Deed Management`,
      msg`Liquidity Pool Access`,
      msg`Smart Contract Escrow`,
      msg`Verified Investment Portal`
    ],
    icon: '/landing/solutions/pione-house.svg',
    href: 'https://pionehouse.com'
  },
  {
    title: msg`Pione Lineage`,
    description: msg`Pione Lineage is a identification and genealogy tracing platform for Vietnamese people, storing ancestral data transparently and securely on the Pione Trace blockchain, helping to preserve roots and connect generations sustainably for the future. It supports long-term preservation of family heritage with privacy-first identity options for community participation.`,
    features: [
      msg`Decentralized Family Tree`,
      msg`Digital Heritage Preservation`,
      msg`Ancestry Record Management`,
      msg`Generational Connectivity`,
      msg`Privacy-focused Identity`
    ],
    icon: '/landing/solutions/pione-lineage.svg',
    href: 'https://dongtoc.com'
  },
  {
    title: msg`Pione Care`,
    description: msg`Personal health record management system based on blockchain, supporting healthcare organizations and the community to digitize data, ensuring information security and improving healthcare efficiency in the digital age. Enables secure, permissioned access across providers to improve continuity of care and reduce administrative overhead.`,
    features: [
      msg`Self-sovereign Health Data`,
      msg`Provider Synchronization`,
      msg`Encrypted Medical Vault`,
      msg`Telemedicine Integration`,
      msg`Secure Diagnostic Records`
    ],
    icon: '/landing/solutions/pione-care.svg',
    href: 'https://pionecare.com'
  },
  {
    title: msg`Pione Swap`,
    description: msg`Pione Swap is a Decentralized Exchange (DEX) platform that allows users to swap, provide liquidity, and trade digital assets and security tokens quickly and efficiently. Built with a focus on transparent execution and flexible liquidity mechanisms for both retail and advanced users.`,
    features: [
      msg`Advanced AMM Model`,
      msg`Liquidity Pool Infrastructure`,
      msg`Yield Optimization Hub`,
      msg`Limit Order Support`,
      msg`Multi-chain Connectivity`
    ],
    icon: '/landing/solutions/pione-swap.svg',
    href: 'https://pioneswap.com'
  },
  {
    title: msg`Pione Zero`,
    description: msg`Gas-free blockchain platform, promoting digital transformation and practical application deployment for businesses in the AI & Blockchain era. Reduces operational barriers for builders and supports scalable deployments with EVM compatibility and developer tooling.`,
    features: [
      msg`Low-fee Infrastructure`,
      msg`Scalable Enterprise Network`,
      msg`Sustainable Protocol Design`,
      msg`EVM Compatibility`,
      msg`Comprehensive Developer SDK`
    ],
    icon: '/landing/solutions/pione-zero.svg',
    href: 'https://zeroscan.org/'
  },
  {
    title: msg`Pione Security`,
    description: msg`Platform supporting businesses in digital transformation of the stock market, issuing and managing on blockchain efficiently. Streamlines STO workflows with investor onboarding, cap table management, and transparent issuance lifecycle tracking.`,
    features: [
      msg`Asset Issuance Lifecycle`,
      msg`Digital Cap Table Management`,
      msg`Secondary Market Access`,
      msg`Verified Investor Onboarding`,
      msg`Automated Distribution`
    ],
    icon: '/landing/solutions/pione-security.svg',
    href: 'https://pionesecurity.com/'
  },
  {
    title: msg`Pione Mart`,
    description: msg`Decentralized e-commerce for safe and convenient shopping. Combines on-chain trust signals and secure payments to improve transparency across the purchase lifecycle.`,
    features: [
      msg`Blockchain Review System`,
      msg`Secure Payment Gateway`,
      msg`Dispute Resolution System`,
      msg`Verified Vendor Identity`,
      msg`Logistics Synchronization`
    ],
    icon: '/landing/solutions/pione-mart.svg',
    href: 'https://pionemart.com'
  },
  {
    title: msg`Pione Dream`,
    description: msg`'Startup Dream' - A platform connecting technology startups with global investors, promoting breakthrough projects. Supports matchmaking, pitching, and fundraising workflows to help teams move from early validation to scalable partnerships.`,
    features: [
      msg`Startup - Investor Matching`,
      msg`Pitch & Fundraising Support`,
      msg`Global Network Access`
    ],
    icon: '/landing/solutions/pione-dream.svg',
    href: 'https://pionedream.com'
  },
  {
    title: msg`ExPione`,
    description: msg`ExPIONE aims for digital asset transactions and access to security tokens, while integrating Artificial Intelligence (AI) to optimize efficiency and enhance security for trading activities. Provides smarter execution, risk-aware decision support, and improved market access for modern traders.`,
    features: [msg`Digital Asset Trading`, msg`Security Token Access`, msg`AI Optimization`],
    icon: '/landing/solutions/ex-pione.svg',
    href: 'https://expione.com'
  },
  {
    title: msg`Pione Charity`,
    description: msg`Decentralized charity fund, applying blockchain to transparentize all contributions, thereby strengthening community trust and responsibility.`,
    features: [msg`Transparent Donations`, msg`Traceable Fund Flows`, msg`Community Trust`],
    icon: '/landing/solutions/pione-charity.svg',
    href: 'https://pionecharity.com'
  },
  {
    title: msg`Pione ID`,
    description: msg`Pione ID (PID) is a decentralized digital identity platform that helps authenticate identities and build trust in digital transactions. The platform allows users to control personal data and secure their identity using blockchain technology.`,
    features: [msg`Decentralized Identity`, msg`User-controlled Data`, msg`Trusted Authentication`],
    icon: '/landing/solutions/pione-id.svg',
    href: 'https://pioneid.com'
  },
  {
    title: msg`Pione Super App`,
    description: msg`Application providing digital transformation solutions for businesses, digital asset management, payment, and transparent tax invoice issuance on the blockchain platform. Unifies everyday financial operations into one experience with secure identity and on-chain records.`,
    features: [
      msg`Multi-service Integration`,
      msg`Digital Payment Gateway`,
      msg`Secure Identity Management`,
      msg`On-chain Billing`,
      msg`App Marketplace`
    ],
    icon: '/landing/solutions/pione-super-app.svg',
    href: '#',
    comingSoon: true
  },
  {
    title: msg`Pione Bot`,
    description: msg`Integrating AI and automated trading.`,
    features: [msg`AI Automation`, msg`Trading Strategies`, msg`Coming Soon`],
    icon: '/landing/solutions/pione-bot.svg',
    href: 'https://pionebot.com',
    comingSoon: true
  },
  {
    title: msg`Pione Game`,
    description: msg`Web3 Blockchain Game platform. Innovating player experience and creating digital economic value.`,
    features: [msg`Web3 Gaming`, msg`Digital Economy`, msg`Coming Soon`],
    icon: '/landing/solutions/pione-game.svg',
    href: 'https://pionegame.com',
    comingSoon: true
  },
  {
    title: msg`Pione Star`,
    description: msg`Pione Star – A transparent and trustworthy social network where users own their data and connect with the community responsibly`,
    features: [msg`User-owned Data`, msg`Community Network`, msg`Coming Soon`],
    icon: '/landing/solutions/pione-star.svg',
    href: 'https://pionestar.com',
    comingSoon: true
  },
  {
    title: msg`Pione Dapps Store`,
    description: msg`Store includes decentralized dApps to help the Web3 community grow strong.`,
    features: [msg`Decentralized dApps`, msg`Community Growth`, msg`Coming Soon`],
    icon: '/landing/solutions/pione-dapp-store.svg',
    href: 'https://pionedappstore.com',
    comingSoon: true
  },
  {
    title: msg`Pione Chain`,
    description: msg`High-performance blockchain infrastructure for enterprise-scale decentralized applications and secure asset management.`,
    features: [msg`Enterprise Infrastructure`, msg`Secure Asset Management`, msg`Coming Soon`],
    icon: '/landing/solutions/pione-chain.svg',
    href: 'https://pionechain.com',
    comingSoon: true
  },
  {
    title: msg`Pione Coin`,
    description: msg`Native utility token of the Pione ecosystem, powering transactions, governance, and incentivizing network participants.`,
    features: [msg`Utility Token`, msg`Governance Support`, msg`Coming Soon`],
    icon: '/landing/solutions/pione-coin.svg',
    href: '#',
    comingSoon: true
  },
  {
    title: msg`Pione Live`,
    description: msg`Decentralized live streaming and real-time communication platform ensuring data privacy and censorship resistance.`,
    features: [msg`Decentralized Streaming`, msg`Data Privacy`, msg`Coming Soon`],
    icon: '/landing/solutions/pione-live.svg',
    href: 'https://pionelive.com',
    comingSoon: true
  },
  {
    title: msg`Pione Node`,
    description: msg`Secure infrastructure nodes powering the Pione network, ensuring high availability and decentralized consensus.`,
    features: [msg`Infrastructure Nodes`, msg`Decentralized Consensus`, msg`Coming Soon`],
    icon: '/landing/solutions/pione-node.svg',
    href: '#',
    comingSoon: true
  },
  {
    title: msg`Pione POAI`,
    description: msg`Proof of Artificial Intelligence - A novel consensus mechanism validating AI-driven computations on the blockchain.`,
    features: [msg`AI Consensus`, msg`Validated Computations`, msg`Coming Soon`],
    icon: '/landing/solutions/pione-poai.svg',
    href: '#',
    comingSoon: true
  },
  {
    title: msg`Pione Scan`,
    description: msg`Comprehensive block explorer for the Pione ecosystem, providing real-time transparency for all on-chain activities. Supports monitoring, verification, and auditing with user-friendly search and on-chain data insights.`,
    features: [msg`Block Explorer`, msg`Real-time Transparency`, msg`Coming Soon`],
    icon: '/landing/solutions/pione-scan.svg',
    href: 'https://pionescan.com',
    comingSoon: true
  }
]

export function Solutions(): React.JSX.Element {
  const { i18n } = useLingui()

  return (
    <GridSection>
      <section id='solutions' className='w-full py-12 md:py-24'>
        <div className='container px-4 md:px-6 rounded-3xl'>
          <SiteHeading
            badge={msg`Solutions`}
            title={msg`Strategic Ecosystem`}
            description={msg`Integrated technology solutions designed to empower modern businesses through digital innovation and blockchain transparency.`}
          />

          <div className='mx-auto grid max-w-6xl grid-cols-1 gap-8 py-10 sm:grid-cols-2 lg:grid-cols-3'>
            {solutionsList.map((item) => (
              <div
                key={i18n._(item.title)}
                className='group relative flex h-full flex-col overflow-hidden rounded-3xl border bg-background/80 p-5 shadow-sm transition-all duration-300 hover:-translate-y-1.5 hover:shadow-lg sm:p-6 md:p-7'
              >
                <div className='pointer-events-none absolute inset-0 opacity-0 transition-opacity duration-300 group-hover:opacity-100'>
                  <div className='absolute -right-24 -top-24 h-56 w-56 rounded-full bg-orange-400/15 blur-2xl' />
                  <div className='absolute -bottom-24 -left-24 h-56 w-56 rounded-full bg-orange-400/10 blur-2xl' />
                </div>
                <div className='mb-5 flex items-start justify-between gap-4 sm:mb-6'>
                  <div className='flex items-center gap-3'>
                    <div className='flex size-14 sm:size-16 items-center justify-center rounded-2xl bg-orange-400/10 ring-1 ring-orange-400/15 transition-colors group-hover:bg-orange-400/15'>
                      <Image
                        src={item.icon}
                        alt={i18n._(item.title)}
                        width={40}
                        height={40}
                        className='h-12 w-12 object-contain'
                      />
                    </div>
                    <h3 className='text-[20px] font-bold leading-tight tracking-tight text-foreground'>
                      {i18n._(item.title)}
                    </h3>
                  </div>
                  {item.comingSoon && (
                    <span className='shrink-0 rounded-full bg-orange-400/10 px-3 py-1 text-xs font-semibold text-orange-700 dark:text-orange-400'>
                      {i18n._(msg`Coming Soon`)}
                    </span>
                  )}
                </div>

                <p className='mb-6 text-[16px] leading-relaxed text-muted-foreground'>
                  {i18n._(item.description)}
                </p>

                <div className='mb-6 space-y-2.5 sm:mb-7'>
                  {item.features.map((feature) => (
                    <div key={i18n._(feature)} className='flex items-start gap-2.5'>
                      <span className='mt-1 size-2 shrink-0 rounded-full bg-orange-400/60' />
                      <span className='text-[15px] font-medium text-foreground/80'>
                        {i18n._(feature)}
                      </span>
                    </div>
                  ))}
                </div>

                <div className='mt-auto grid grid-cols-2 gap-3 border-t pt-5'>
                  {item.href !== '#' ?
                    <Link
                      href={item.href}
                      target={item.href.startsWith('http') ? '_blank' : undefined}
                      rel={item.href.startsWith('http') ? 'noopener noreferrer' : undefined}
                      className='w-full'
                    >
                      <StarBorder className='w-full justify-center py-3 text-[12px] font-bold uppercase tracking-widest'>
                        {i18n._(msg`Website`)}
                      </StarBorder>
                    </Link>
                    : <div className='w-full'>
                      <StarBorder className='w-full justify-center py-3 text-[12px] font-bold uppercase tracking-widest opacity-60'>
                        {i18n._(msg`Coming Soon`)}
                      </StarBorder>
                    </div>
                  }
                  <Link href={Routes.Contact} className='w-full'>
                    <StarBorder className='w-full justify-center py-3 text-[12px] font-bold uppercase tracking-widest'>
                      {i18n._(msg`Contact`)}
                    </StarBorder>
                  </Link>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>
    </GridSection>
  )
}
